import { Button, Typography, Card, Space, Alert, Spin, Checkbox, Result } from 'antd';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { requestAccountDeletion } from '../utils/ez_api/accountDeletionAPIs';
import { showNotification } from '../utils/notificationUtil';
import { clearLocalStoragePayload } from '../utils/ez_api/authUtil';
import '../styles/login.scss';

const { Title, Text, Paragraph } = Typography;

export const AccountDeletionConfirm = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [deletionSuccess, setDeletionSuccess] = useState(false);
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        // Check if user is logged in
        if (!localStorage.getItem('jwt')) {
            history.push('/login?redirect=delete-account-confirm');
        }
    }, [history]);

    useEffect(() => {
        if (deletionSuccess && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (deletionSuccess && countdown === 0) {
            // Log out user
            clearLocalStoragePayload();
            history.push('/');
        }
    }, [deletionSuccess, countdown, history]);

    const handleDeleteAccount = async () => {
        if (!confirmed) {
            showNotification(
                'warning',
                'Please confirm that you understand the consequences',
                'Confirmation Required'
            );
            return;
        }

        setLoading(true);
        try {
            await requestAccountDeletion();
            setDeletionSuccess(true);
            showNotification('success', 'Account deletion has been scheduled', 'Success');
        } catch (error: any) {
            showNotification(
                'error',
                error?.response?.data?.message || 'Failed to request account deletion',
                'Error'
            );
            setLoading(false);
        }
    };

    if (deletionSuccess) {
        return (
            <div className="login-container">
                <Card style={{ maxWidth: 700, margin: '0 auto', padding: '16px' }}>
                    <Result
                        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        status="success"
                        title="Account Deletion Scheduled Successfully"
                        extra={[
                            <Alert
                                key="info"
                                message="You can still cancel"
                                description="You can cancel this request by contacting support in during the 14-day grace period. After that, all your data will be permanently deleted."
                                type="info"
                                showIcon
                                style={{ textAlign: 'left' }}
                            />,
                        ]}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className="login-container">
            <Spin spinning={loading} tip="Processing...">
                <Card style={{ maxWidth: 700, margin: '0 auto', padding: '16px' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div style={{ textAlign: 'center' }}>
                            <WarningOutlined
                                style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 12 }}
                            />
                            <Title level={3} style={{ marginBottom: 4, marginTop: 0 }}>
                                Final Confirmation Required
                            </Title>
                        </div>

                        <Alert
                            message="This action cannot be undone"
                            description="By proceeding, your account will be scheduled for permanent deletion in 14 days. You will receive a confirmation email with further details."
                            type="error"
                            showIcon
                        />

                        <div>
                            <Title level={4} style={{ marginBottom: 8 }}>
                                Before you proceed...
                            </Title>
                            <Paragraph style={{ marginBottom: 0 }}>
                                Please make sure you have:
                                <ul style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
                                    <li>Downloaded or backed up any important documents or data</li>
                                    <li>Saved contact information of clients or team members</li>
                                    <li>Notified your team members about the account closure</li>
                                    <li>Cancelled or transferred any active subscriptions</li>
                                </ul>
                            </Paragraph>
                        </div>

                        <Card style={{ background: '#fafafa', padding: '12px' }}>
                            <Checkbox
                                checked={confirmed}
                                onChange={e => setConfirmed(e.target.checked)}
                            >
                                <Text strong>
                                    I understand that my account and all associated data will be
                                    permanently deleted after 14 days, and this action cannot be
                                    undone. I acknowledge that I can cancel this request within the
                                    14-day grace period by contacting support.
                                </Text>
                            </Checkbox>
                        </Card>

                        <Space style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                            <Button size="large" onClick={() => history.push('/home')}>
                                Cancel & Go Back
                            </Button>
                            <Button
                                type="primary"
                                danger
                                size="large"
                                disabled={!confirmed}
                                onClick={handleDeleteAccount}
                            >
                                Confirm Account Deletion
                            </Button>
                        </Space>

                        <div style={{ textAlign: 'center', marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Need help? <a href="/home">Contact Support</a>
                            </Text>
                        </div>
                    </Space>
                </Card>
            </Spin>
        </div>
    );
};
